import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useCurrentUserCompany } from "@/hooks/useCurrentUserCompany";
import { useLanguage } from "@/contexts/LanguageContext";
import { distributionService } from "@/services/distribution/distributionService";
import { DistributionData, DistributionPayload, DistributionUpdatePayload } from "@/types/distribution/distribution.types";
import { TransportDistribution } from "@/types/transport-distribution/transport-distribution.types";
import { FootprintProduct } from "@/types/results";
import { useToast } from "@/components/ui/use-toast";

export const useDistributionData = () => {
  const { user } = useAuth();
  const { company } = useCurrentUserCompany();
  const { t } = useLanguage();
  const { toast } = useToast();
  
  const [distributionData, setDistributionData] = useState<DistributionData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch distribution data for the company
  const fetchDistributionData = async () => {
    if (!company?.id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const data = await distributionService.getDistributionByCompany(company.id);
      setDistributionData(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to fetch distribution data";
      setError(errorMessage);
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Create new distribution data
  const createDistribution = async (
    payload: DistributionPayload,
    transportDistributions: TransportDistribution[],
    product: FootprintProduct,
    companyType: string = "transformation"
  ): Promise<string | null> => {
    if (!user?.uid || !company?.id) {
      toast({
        title: "Error",
        description: "User or company not found",
        variant: "destructive",
      });
      return null;
    }

    try {
      const distributionId = await distributionService.createDistribution(
        payload, 
        user.uid, 
        company.id,
        transportDistributions,
        product,
        companyType
      );
      
      // Refresh the data
      await fetchDistributionData();
      
      toast({
        title: t("results.distribution.carbon.success.distributionCalculated"),
        description: t("results.distribution.carbon.descriptions.distributionCalculated"),
      });
      
      return distributionId;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to create distribution data";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
      return null;
    }
  };

  // Update existing distribution data
  const updateDistribution = async (
    distributionId: string, 
    payload: DistributionUpdatePayload,
    transportDistributions: TransportDistribution[],
    product: FootprintProduct,
    companyType: string = "transformation",
    footprintType: 'carbon' | 'hydric' | 'both' = 'both'
  ): Promise<boolean> => {
    if (!user?.uid) {
      toast({
        title: "Error",
        description: "User not found",
        variant: "destructive",
      });
      return false;
    }

    try {
      await distributionService.updateDistribution(
        distributionId, 
        payload, 
        user.uid,
        transportDistributions,
        product,
        companyType,
        footprintType
      );
      
      // Refresh the data
      await fetchDistributionData();
      
      toast({
        title: t("results.distribution.carbon.success.distributionUpdated"),
        description: t("results.distribution.carbon.descriptions.distributionUpdated"),
      });
      
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to update distribution data";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
      return false;
    }
  };

  // Delete distribution data
  const deleteDistribution = async (distributionId: string, productId?: string): Promise<boolean> => {
    try {
      await distributionService.deleteDistribution(distributionId, productId);
      
      // Refresh the data
      await fetchDistributionData();
      
      toast({
        title: t("results.distribution.carbon.success.distributionDeleted"),
        description: t("results.distribution.carbon.descriptions.distributionDeleted"),
      });
      
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Failed to delete distribution data";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
      return false;
    }
  };

  // Get distribution data by product
  const getDistributionByProduct = (productId: string): DistributionData[] => {
    return distributionData.filter(dist => dist.product_id === productId);
  };

  // Get distribution data by footprint type
  const getDistributionByFootprintType = (footprintType: 'carbon' | 'hydric'): DistributionData[] => {
    return distributionData.filter(dist => dist.footprint_type === footprintType);
  };

  // Check if product has distribution data
  const hasDistributionData = (productId: string): boolean => {
    return distributionData.some(dist => dist.product_id === productId);
  };

  // Fetch data on mount and when company changes
  useEffect(() => {
    if (company?.id) {
      fetchDistributionData();
    }
  }, [company?.id]);

  return {
    distributionData,
    loading,
    error,
    fetchDistributionData,
    createDistribution,
    updateDistribution,
    deleteDistribution,
    getDistributionByProduct,
    getDistributionByFootprintType,
    hasDistributionData,
  };
};
